import * as THREE from 'three';
import { Entity, bossHpScale, type EntityCtx } from '../Entity';
import { Explosion } from './Explosion';
import { Bullet } from './Bullet';
import { makeGlowSprite } from './energyGlow';
import { makeCloudTexture } from '../Scene';
import { useStore } from '../../state/store';

/**
 * Maelstrom Engine — a stationary stone obelisk crowned with a copper turbine and four
 * lightning rods at the corners. Periodically launches lightning bolts at the player.
 * Invulnerable until ALL FOUR lightning rods are shot off, after which the turbine eye
 * is exposed and can be destroyed.
 */
const TURBINE_HP = 60;
const ROD_HP = 10;
const BOLT_INTERVAL = 1.4;
const BOLT_RANGE = 1100;
const SCORE = 7500;

interface Rod {
  id: string;
  group: THREE.Group;
  hp: number;
  destroyed: boolean;
}

type Stage = 'active' | 'detonating' | 'done';

export class MaelstromEngine extends Entity {
  stage: Stage = 'active';
  forward = new THREE.Vector3(0, 0, -1);
  readonly speed = 0;
  private turbineHp = Math.round(TURBINE_HP * bossHpScale());
  private turbine: THREE.Mesh;
  private turbineCore: THREE.Mesh;
  private turbineSpinner: THREE.Object3D;
  private stormCrown: THREE.Group;
  private rods: Rod[] = [];
  private boltCooldown = 1.4;
  private dieTimer = 0;
  private weatherTimer = 0;
  private surgeTimer = 3;

  constructor(spawnPos: THREE.Vector3) {
    super('tank');
    this.isBoss = true;
    this.hp = 999;
    this.radius = 30;
    this.group.position.copy(spawnPos);

    const built = makeMaelstromMesh();
    this.group.add(built.mesh);
    this.turbine = built.turbine;
    this.turbineCore = built.turbineCore;
    this.turbineSpinner = built.turbineSpinner;
    this.stormCrown = built.stormCrown;
    this.rods = built.rods;
    for (const p of this.rods) p.hp = Math.round(p.hp * bossHpScale()); // later waves, tougher parts

    this.hitParts = [
      { id: 'turbine', box: new THREE.Box3(new THREE.Vector3(-3, -3, -3), new THREE.Vector3(3, 3, 3)), object: this.turbine },
      ...this.rods.map((r) => ({
        id: r.id,
        box: new THREE.Box3(new THREE.Vector3(-1.2, -8, -1.2), new THREE.Vector3(1.2, 8, 1.2)),
        object: r.group,
      })),
    ];
  }

  alive(): boolean { return this.stage === 'active'; }

  damagePart(partId: string, amount: number, ctx: EntityCtx): void {
    if (this.stage !== 'active') return;
    if (partId === 'turbine') {
      // Invulnerable while any rod is up — blue shield spark + name the weak point.
      if (this.rods.some((r) => !r.destroyed)) {
        const wp = new THREE.Vector3();
        this.turbine.getWorldPosition(wp);
        ctx.game.shieldedFeedback(ctx, wp, 'SHIELDED — SHOOT OFF THE 4 LIGHTNING RODS');
        return;
      }
      this.turbineHp -= amount;
      const wp = new THREE.Vector3();
      this.turbine.getWorldPosition(wp);
      new Explosion(wp, 5, 0.25, 'fire').addTo(ctx);
      if (this.turbineHp <= 0) this.beginDying(ctx);
      return;
    }
    // (No obelisk part — misses pass through.)
    const rod = this.rods.find((rr) => rr.id === partId);
    if (rod && !rod.destroyed) {
      rod.hp -= amount;
      const wp = new THREE.Vector3();
      rod.group.getWorldPosition(wp);
      new Explosion(wp, 4, 0.2, 'fire').addTo(ctx);
      if (rod.hp <= 0) {
        rod.destroyed = true;
        new Explosion(wp, 26, 0.7, 'fire').addTo(ctx);
        new Explosion(wp, 18, 3.5, 'smoke').setDrift(new THREE.Vector3(0, 9, 0)).addTo(ctx);
        ctx.game.audio.playBoom(0.6);
        rod.group.visible = false;
        if (this.hitParts) this.hitParts = this.hitParts.filter((p) => p.id !== rod.id);
      }
    }
  }

  update(ctx: EntityCtx): void {
    const { dt } = ctx;
    if (this.stage === 'detonating') { this.updateDying(ctx); return; }
    if (this.stage === 'done') { this.dead = true; return; }

    // Spin turbine + pulse the core
    this.turbineSpinner.rotation.y += dt * 1.6;
    const core = this.turbineCore.material as THREE.MeshBasicMaterial;
    const pulse = 0.5 + 0.5 * Math.sin(performance.now() * 0.012);
    core.color.setRGB(0.9 * pulse, 0.6, 1.0);

    // Lightning bolts at the player. ESCALATES per rod destroyed: every rod the player strips
    // intensifies the storm (~doubled fire rate by the time the turbine is exposed).
    const rodsDown = this.rods.filter((r) => r.destroyed).length;
    const boltScale = Math.pow(0.85, rodsDown);
    this.boltCooldown -= dt;
    const playerDist = ctx.game.player.position.distanceTo(this.group.position);
    if (playerDist < BOLT_RANGE && this.boltCooldown <= 0) {
      this.boltCooldown = BOLT_INTERVAL * boltScale + Math.random() * 0.4;
      this.fireBolt(ctx);
    }

    // Exposed-turbine phase: periodic STORM SURGE — a radial ring of bolts erupts from the
    // turbine; weave between the spokes while you finish it. The final fight is a dodge, not
    // an execution.
    if (rodsDown === this.rods.length) {
      this.surgeTimer -= dt;
      if (this.surgeTimer <= 0) {
        this.surgeTimer = 6;
        const origin = new THREE.Vector3();
        this.turbine.getWorldPosition(origin);
        for (let k = 0; k < 6; k++) {
          const a = (k / 6) * Math.PI * 2;
          const dir = new THREE.Vector3(Math.cos(a), 0.22, Math.sin(a)).normalize();
          ctx.game.add(new Bullet(origin.clone().addScaledVector(dir, 4), dir.clone().multiplyScalar(240), this.id, 'enemy'));
        }
        new Explosion(origin, 20, 0.3, 'shield').addTo(ctx);
        ctx.game.audio.playBoom(0.6);
      }
    }

    // The storm crown churns overhead — faster as the storm intensifies.
    this.stormCrown.rotation.y += dt * (0.12 + rodsDown * 0.06);

    // Weather — frequent lightning around the engine; occasionally a full vertical strike
    // from the crown to the ground so the summit reads as the eye of a storm.
    this.weatherTimer += dt;
    if (this.weatherTimer >= 0.45) {
      this.weatherTimer = 0;
      const off = new THREE.Vector3(
        (Math.random() - 0.5) * 150,
        Math.random() * 70,
        (Math.random() - 0.5) * 150,
      );
      new Explosion(this.group.position.clone().add(off), 13, 0.18, 'shield').addTo(ctx);
      if (Math.random() < 0.3) {
        // Sky-to-ground strike: a stacked column of flashes under the crown.
        const sx = (Math.random() - 0.5) * 120, sz = (Math.random() - 0.5) * 120;
        for (let h = 0; h < 5; h++) {
          new Explosion(this.group.position.clone().add(new THREE.Vector3(sx, 18 + h * 22, sz)), 9, 0.16, 'shield').addTo(ctx);
        }
      }
    }
  }

  private fireBolt(ctx: EntityCtx) {
    // Bolt origin at the turbine top.
    const muzzle = new THREE.Vector3();
    this.turbine.getWorldPosition(muzzle);
    const dir = ctx.game.player.position.clone().sub(muzzle).normalize();
    dir.x += (Math.random() - 0.5) * 0.04;
    dir.y += (Math.random() - 0.5) * 0.04;
    dir.z += (Math.random() - 0.5) * 0.04;
    dir.normalize();
    const vel = dir.multiplyScalar(500);
    ctx.game.add(new Bullet(muzzle, vel, this.id, 'enemy'));
    new Explosion(muzzle, 5, 0.18, 'fire').addTo(ctx);
  }

  private beginDying(ctx: EntityCtx) {
    this.stage = 'detonating';
    this.dieTimer = 0;
    useStore.getState().registerKill(SCORE);
    const wp = new THREE.Vector3();
    this.turbine.getWorldPosition(wp);
    new Explosion(wp, 70, 1.0, 'fire').addTo(ctx);
    ctx.game.audio.playHydrogenRoar();
  }

  private updateDying(ctx: EntityCtx) {
    this.dieTimer += ctx.dt;
    const t = this.dieTimer;
    // Turbine over-spins (visualize: random sideways arcs)
    if (t < 1.0) {
      this.turbineSpinner.rotation.y += ctx.dt * 12;
      if (Math.random() < 0.5) {
        const wp = new THREE.Vector3();
        this.turbine.getWorldPosition(wp);
        for (let a = 0; a < 4; a++) {
          const angle = Math.random() * Math.PI * 2;
          const off = new THREE.Vector3(Math.cos(angle) * 30, Math.random() * 8 - 4, Math.sin(angle) * 30);
          new Explosion(wp.clone().add(off), 8, 0.3, 'fire').addTo(ctx);
        }
      }
    }
    // Obelisk shatter at t=1.0
    if (t >= 1.0 && t < 1.1) {
      const wp = this.group.position.clone();
      wp.y += 10;
      new Explosion(wp, 180, 1.0, 'fire').addTo(ctx);
      new Explosion(wp, 120, 0.6, 'fire').addTo(ctx);
      ctx.game.audio.playBoom(1.4);
      // Remove the obelisk visually — collapse rotation
      this.group.rotation.x = 0.4;
      this.group.position.y -= 6;
    }
    // Lingering smoke pillar
    if (t > 1.0 && t < 5 && Math.random() < 0.4) {
      const wp = this.group.position.clone();
      const off = new THREE.Vector3((Math.random() - 0.5) * 30, 5 + Math.random() * 25, (Math.random() - 0.5) * 30);
      new Explosion(wp.add(off), 36, 5, 'smoke').setDrift(new THREE.Vector3(0, 14, 0)).addTo(ctx);
    }
    if (t > 5) this.stage = 'done';
  }
}

/* ---------------------- mesh ---------------------- */

interface MBuilt { mesh: THREE.Group; turbine: THREE.Mesh; turbineCore: THREE.Mesh; turbineSpinner: THREE.Object3D; rods: Rod[]; stormCrown: THREE.Group; }

function makeMaelstromMesh(): MBuilt {
  const g = new THREE.Group();
  const stone = new THREE.MeshStandardMaterial({ color: 0x6c625a, roughness: 0.95 });
  const stoneDark = new THREE.MeshStandardMaterial({ color: 0x3a342e, roughness: 0.95 });
  const copper = new THREE.MeshStandardMaterial({ color: 0xc97a3c, roughness: 0.4, metalness: 0.85 });
  const ironDark = new THREE.MeshStandardMaterial({ color: 0x252528, roughness: 0.45, metalness: 0.7 });
  const dim = new THREE.MeshStandardMaterial({ color: 0x100b08, roughness: 1 });
  const bolt = new THREE.MeshBasicMaterial({ color: 0xb284ff });

  // Plinth (square base)
  const plinth = new THREE.Mesh(new THREE.BoxGeometry(18, 4, 18, 4, 2, 4), stoneDark);
  plinth.position.set(0, 2, 0);
  g.add(plinth);
  // Obelisk shaft tapering up
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(2.5, 4.5, 30, 6, 6), stone);
  shaft.position.set(0, 19, 0);
  g.add(shaft);
  // Cap on top
  const cap = new THREE.Mesh(new THREE.CylinderGeometry(3.5, 3.5, 1.5, 8, 1), stoneDark);
  cap.position.set(0, 35, 0);
  g.add(cap);

  // 4 lightning rods at the corners of the plinth
  const rods: Rod[] = [];
  const rodOff = 7;
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    const rg = new THREE.Group();
    // Rod shaft
    const rod = new THREE.Mesh(new THREE.CylinderGeometry(0.4, 0.4, 18, 12), copper);
    rod.position.set(0, 9, 0);
    rg.add(rod);
    // Tip ball
    const tip = new THREE.Mesh(new THREE.SphereGeometry(0.85, 16, 12), copper);
    tip.position.set(0, 18.5, 0);
    rg.add(tip);
    // Brass coil at base
    for (let c = 0; c < 4; c++) {
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.7, 0.15, 8, 16), copper);
      ring.rotation.x = Math.PI / 2;
      ring.position.set(0, 1 + c * 0.5, 0);
      rg.add(ring);
    }
    rg.position.set(Math.cos(a) * rodOff, 4, Math.sin(a) * rodOff);
    g.add(rg);
    rods.push({ id: `rod${i}`, group: rg, hp: ROD_HP, destroyed: false });
  }

  // Turbine on top of cap — copper vanes that spin, with a glowing core.
  const turbineSpinner = new THREE.Group();
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const vane = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.18, 4, 1, 1, 4), copper);
    vane.position.set(Math.cos(a) * 2.1, 0, Math.sin(a) * 2.1);
    vane.rotation.y = a + 0.3;
    turbineSpinner.add(vane);
  }
  // Hub
  const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 0.9, 0.7, 16), ironDark);
  turbineSpinner.add(hub);
  turbineSpinner.position.set(0, 37, 0);
  g.add(turbineSpinner);
  // Turbine "eye" (kill point) — sphere just above the hub
  const turbine = new THREE.Mesh(new THREE.SphereGeometry(1.6, 32, 24), dim);
  turbine.position.set(0, 39, 0);
  g.add(turbine);
  // Glowing core inside
  const turbineCore = new THREE.Mesh(new THREE.SphereGeometry(0.95, 32, 24), bolt);
  turbineCore.position.copy(turbine.position);
  turbineCore.add(makeGlowSprite(0xb98cff, 6.0));
  g.add(turbineCore);

  // Storm crown — a slowly-rotating ring of dark thunderheads overhead. The FINAL boss should
  // own the sky around it, not sit politely on a hill.
  const stormCrown = new THREE.Group();
  const stormMat = new THREE.SpriteMaterial({
    map: makeCloudTexture(), transparent: true, opacity: 0.85,
    color: 0x2e3442, depthWrite: false,
  });
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2;
    const r = 26 + (i % 3) * 12;
    const s = new THREE.Sprite(stormMat);
    s.position.set(Math.cos(a) * r, 56 + (i % 2) * 7, Math.sin(a) * r);
    const sc = 55 + (i % 3) * 22;
    s.scale.set(sc, sc * 0.45, 1);
    stormCrown.add(s);
  }
  g.add(stormCrown);

  // Finale scale — the wave-24 boss towers (~72u) instead of reading as a garden obelisk.
  g.scale.setScalar(1.8);
  return { mesh: g, turbine, turbineCore, turbineSpinner, rods, stormCrown };
}
